/**
 * FeedbackButton Component
 *
 * Renders a feedback trigger button for each evidence item in the review interface.
 * When clicked, triggers a callback to open the feedback form for the specific item.
 * Shows visual indication if feedback has already been provided.
 *
 * Requirements: 8.1
 */

import React from 'react';
import {
  CommentEdit20Regular,
  CommentEdit20Filled,
  Checkmark20Regular,
} from '@fluentui/react-icons';
import styles from './FeedbackButton.module.css';

export interface FeedbackButtonProps {
  /** Unique identifier for the evidence item */
  itemId: string;
  /** Callback triggered when the button is clicked */
  onClick: (itemId: string) => void;
  /** Whether feedback has already been provided for this item */
  hasFeedback?: boolean;
  /** Whether the feedback indicates the value was correct */
  feedbackIsCorrect?: boolean;
  /** Whether the button is disabled */
  disabled?: boolean;
  /** Optional custom aria-label for accessibility */
  ariaLabel?: string;
  /** Size variant of the button */
  size?: 'small' | 'medium';
}

/**
 * FeedbackButton displays a clickable button/icon that indicates feedback can be provided
 * for an evidence item. It shows different visual states based on whether feedback
 * has been provided and whether the feedback marked the item as correct or incorrect.
 *
 * Visual States:
 * - No feedback: Shows outline comment icon
 * - Feedback provided (correct): Shows checkmark with success styling
 * - Feedback provided (incorrect): Shows filled comment icon with warning styling
 *
 * @example
 * // Basic usage - no feedback yet
 * <FeedbackButton
 *   itemId="evidence-1"
 *   onClick={(id) => openFeedbackForm(id)}
 * />
 *
 * @example
 * // With feedback already provided
 * <FeedbackButton
 *   itemId="evidence-1"
 *   onClick={(id) => openFeedbackForm(id)}
 *   hasFeedback={true}
 *   feedbackIsCorrect={false}
 * />
 */
export const FeedbackButton: React.FC<FeedbackButtonProps> = ({
  itemId,
  onClick,
  hasFeedback = false,
  feedbackIsCorrect,
  disabled = false,
  ariaLabel,
  size = 'medium',
}) => {
  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!disabled) {
      onClick(itemId);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      if (!disabled) {
        onClick(itemId);
      }
    }
  };

  // Determine the button state and styling
  const getButtonState = (): {
    className: string;
    icon: React.ReactNode;
    title: string;
    label: string;
  } => {
    if (hasFeedback) {
      if (feedbackIsCorrect) {
        return {
          className: `${styles.button} ${styles.correct} ${styles[size]}`,
          icon: <Checkmark20Regular />,
          title: 'Feedback provided: Marked as correct',
          label: 'Feedback provided - marked as correct. Click to edit feedback.',
        };
      } else {
        return {
          className: `${styles.button} ${styles.corrected} ${styles[size]}`,
          icon: <CommentEdit20Filled />,
          title: 'Feedback provided: Correction submitted',
          label: 'Feedback provided - correction submitted. Click to edit feedback.',
        };
      }
    }

    return {
      className: `${styles.button} ${styles.default} ${styles[size]}`,
      icon: <CommentEdit20Regular />,
      title: 'Provide feedback',
      label: 'Provide feedback for this evidence item',
    };
  };

  const { className, icon, title, label } = getButtonState();

  return (
    <button
      type="button"
      className={`${className} ${disabled ? styles.disabled : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      disabled={disabled}
      title={title}
      aria-label={ariaLabel || label}
      aria-pressed={hasFeedback}
      data-item-id={itemId}
      data-has-feedback={hasFeedback}
    >
      <span className={styles.iconWrapper} aria-hidden="true">
        {icon}
      </span>
      {hasFeedback && (
        <span className={styles.indicator} aria-hidden="true" />
      )}
    </button>
  );
};

export default FeedbackButton;
